import { Slot, AdsSlice } from 'types/State';
import { createAction, createReducer, combineReducers } from '@reduxjs/toolkit';
import * as googleAds from 'lib/GoogleAds';
import { reject } from 'lodash';
import { SerializedAdConfig } from 'types/AdConfig';

const adConfigReceived = createAction<{
  adUnit: string;
  adTargeting: googleAds.Targeting;
  adsDisabled: boolean;
}>('googleAds/adConfigReceived');

const adConfigUnloaded = createAction('googleAds/adConfigUnloaded');

const clearTargetingEnqueued = createAction('googleAds/clearTargetingEnqueued');

const suggestiveSellingAdDisplayEnqueued = createAction(
  'googleAds/suggestiveSellingAdDisplayEnqueued'
);

const suggestiveSellingAdDivsCreated = createAction('googleAds/suggestiveSellingAdDivsCreated');

const slotNoLongerHasComponent = createAction<{ elementId: string }>(
  'googleAds/slotNoLongerHasComponent'
);

const dataProcessingRestrictionEnqueued = createAction<{
  wasRestricted: boolean;
}>('googleAds/dataProcessingRestrictionEnqueued');

const setPageTargetingEnqueued = createAction<googleAds.Targeting>(
  'googleAds/setPageTargetingEnqueued'
);

const enableServiceEnqueued = createAction('googleAds/enableServiceEnqueued');

const suggestiveSellingAdComponentReadyToLoad = createAction(
  'googleAds/suggestiveSellingAdComponentReadyToLoad'
);

const adComponentReadyToLoad = createAction<SerializedAdConfig>('googleAds/adComponentReadyToLoad');

const adComponentReadyToReload = createAction<Omit<SerializedAdConfig, 'elementId' | 'adUnit'>>(
  'googleAds/adComponentReadyToReload'
);

const adComponentTearingdown = createAction<Omit<SerializedAdConfig, 'elementId' | 'adUnit'>>(
  'googleAds/adComponentTearingdown'
);

const slotDestructionEnqueued = createAction<Partial<SerializedAdConfig>>(
  'googleAds/slotDestructionEnqueued'
);

const slotNotDisplayed = createAction<{
  config: SerializedAdConfig;
  reason: string;
}>('googleAds/slotNotDisplayed');

const suggestiveSellingComponentTearingDown = createAction(
  'googleAds/suggestiveSellingComponentTearingDown'
);

const ensightenVarsSet = createAction<any>('googleAds/ensightenVarsSet');

const slotRenderEnded = createAction<{
  elementId: string;
  isEmpty: boolean;
  isSuggestive: boolean;
}>('googleAds/slotRenderEnded');

const slotDefinitionEnqueued = createAction<any>('googleAds/slotDefinitionEqueued');

const suggestiveSellingAdLoaded = createAction<any>('googleAds/suggestiveSellingAdLoaded');

const slotDisplayEnqueued = createAction<any>('googleAds/slotDisplayEnqueued');

const targetingReducer = createReducer<googleAds.Targeting>(
  {},
  {
    [adConfigReceived.type]: (state, action) => {
      return action.payload.adTargeting;
    },
    [adConfigUnloaded.type]: () => {
      return {};
    },
  }
);

const slotsReducer = createReducer<Slot[]>([], {
  [slotRenderEnded.type]: (state, action: ReturnType<typeof slotRenderEnded>) => {
    return [...state, action.payload];
  },
  [slotDestructionEnqueued.type]: (state, action) => {
    // remove the slot from the list of slots
    return reject(state, (slot) => slot.elementId === action.payload.elementId);
  },
});

const suggestiveSellingAdsReducer = createReducer<any[]>([], {
  [suggestiveSellingAdLoaded.type]: (state, action) => {
    return [...state, action.payload];
  },
  [suggestiveSellingComponentTearingDown.type]: () => {
    return [];
  },
});

const adUnitReducer = createReducer<string | null>(null, {
  [adConfigReceived.type]: (_state, action) => {
    return action.payload.adUnit;
  },
});

const googleAdsReducer = combineReducers<AdsSlice>({
  targeting: targetingReducer,
  slots: slotsReducer,
  suggestiveSellingAds: suggestiveSellingAdsReducer,
  adUnit: adUnitReducer,
});

export {
  clearTargetingEnqueued,
  dataProcessingRestrictionEnqueued,
  setPageTargetingEnqueued,
  ensightenVarsSet,
  enableServiceEnqueued,
  googleAdsReducer,
  adComponentReadyToLoad,
  slotDefinitionEnqueued,
  slotDisplayEnqueued,
  slotRenderEnded,
  slotNotDisplayed,
  adComponentTearingdown,
  slotDestructionEnqueued,
  adComponentReadyToReload,
  suggestiveSellingAdLoaded,
  suggestiveSellingAdComponentReadyToLoad,
  suggestiveSellingComponentTearingDown,
  slotNoLongerHasComponent,
  suggestiveSellingAdDivsCreated,
  suggestiveSellingAdDisplayEnqueued,
  adConfigReceived,
  adConfigUnloaded,
};
